/**
 * The schedules list (HS-1603): URL search, filter fields and the one mapping onto
 * `GET /api/reports/schedules` (HS-891).
 */
import { DEFAULT_PAGE, sortSpecSchema } from '@smartsash/data-screen';
import type { FilterCondition } from '@smartsash/ui';
import { z } from 'zod';

import { REPORTS_MAX_PAGE_SIZE, SCHEDULE_STATUSES } from '../reports.constants';
import type { ReportScheduleDto, ReportSchedulesParams, RunStatus } from '../reports.types';
import { isOneOf } from '../reportsGuards';
import { labNameOf } from '../reportsModel';

import { adminListShape, LIST_PAGE_SIZE } from '@/router/listSearch';

export const SCHEDULES_TEMPLATE_FIELD = 'template';
export const SCHEDULES_STATUS_FIELD = 'status';

export const schedulesSearchSchema = z.object({
  page: z.coerce.number().int().positive().catch(DEFAULT_PAGE),
  pageSize: z.coerce
    .number()
    .int()
    .positive()
    .catch(LIST_PAGE_SIZE)
    .transform((size) => Math.min(size, REPORTS_MAX_PAGE_SIZE)),
  // The endpoint orders by next run; `sort` is accepted only so a stale link does not fail.
  sort: sortSpecSchema.optional().catch(undefined),
  q: z.string().trim().optional().catch(undefined),
  filters: adminListShape.filters,
});

export type SchedulesSearch = z.infer<typeof schedulesSearchSchema>;

function equalsValue(conditions: readonly FilterCondition[], field: string): string | undefined {
  const value = conditions.find((item) => item.field === field && item.operator === 'eq')
    ?.values[0];
  return value === undefined || value === '' ? undefined : value;
}

export function schedulesQueryParams(search: SchedulesSearch): ReportSchedulesParams {
  const conditions = search.filters ?? [];
  const params: ReportSchedulesParams = {
    page: search.page,
    pageSize: Math.min(search.pageSize, REPORTS_MAX_PAGE_SIZE),
  };
  if (search.q !== undefined && search.q !== '') params.search = search.q;
  const template = equalsValue(conditions, SCHEDULES_TEMPLATE_FIELD);
  if (template !== undefined) params.reportType = [template];
  const status = equalsValue(conditions, SCHEDULES_STATUS_FIELD);
  if (status !== undefined && isOneOf(SCHEDULE_STATUSES, status)) params.status = [status];
  return params;
}

export const scheduleColumnText = {
  template: (row: ReportScheduleDto) => row.templateKey,
  institution: (row: ReportScheduleDto) => row.institution.name,
  laboratory: (row: ReportScheduleDto) => labNameOf(row.scope) ?? '',
  runTime: (row: ReportScheduleDto) => `${row.frequency} ${row.runAt}`,
  formats: (row: ReportScheduleDto) => row.formats.join(' '),
  recipients: (row: ReportScheduleDto) => String(row.recipients.length),
  nextRun: (row: ReportScheduleDto) => row.nextRunAt ?? '',
  lastRun: (row: ReportScheduleDto) => row.lastRun?.createdAt ?? '',
  status: (row: ReportScheduleDto) => row.status,
} as const;

export type ScheduleColumnId = keyof typeof scheduleColumnText;

/**
 * The Last run cell names the run's status only when it went wrong (S6, QB25): a Ready run is the
 * normal case and the tag alone says it; a Failed or Expired one needs the badge to be noticed.
 */
export function lastRunShowsStatus(status: RunStatus): boolean {
  return status === 'Failed' || status === 'Expired';
}

/**
 * What the Next run cell prints (S5): the instant, "Paused" while the schedule is paused (C-7: the
 * contract sends no next run then), and nothing for an ended schedule or a missing instant.
 */
export type NextRunState = { kind: 'at'; at: string } | { kind: 'paused' } | { kind: 'none' };

export function nextRunState(row: Pick<ReportScheduleDto, 'status' | 'nextRunAt'>): NextRunState {
  if (row.status === 'Paused') return { kind: 'paused' };
  if (row.status === 'Ended' || row.nextRunAt === null) return { kind: 'none' };
  return { kind: 'at', at: row.nextRunAt };
}

/** A search or a filter narrows the list, so an empty page means "no match", not "none yet". */
export function isNarrowed(search: Pick<SchedulesSearch, 'q' | 'filters'>): boolean {
  return (search.q !== undefined && search.q !== '') || (search.filters ?? []).length > 0;
}
